<template>
  <el-dialog
    :title="dialogTitle"
    :model-value="dialogVisible"
    width="550px"
    :close-on-click-modal="false"
    @close="handleClose"
  >
    <el-form ref="formRef" :model="formData" :rules="formRules" label-width="110px">
      <el-form-item label="域名/AppID" prop="domain">
        <el-input v-model="formData.domain" placeholder="请输入域名或AppID" />
      </el-form-item>
      <el-form-item label="域名类型" prop="domainType">
        <el-select v-model="formData.domainType" placeholder="请选择域名类型" style="width: 100%">
          <el-option label="H5域名" :value="DomainTypeEnum.H5" />
          <el-option label="小程序AppID" :value="DomainTypeEnum.MINI_APP" />
          <el-option label="App包名" :value="DomainTypeEnum.APP" />
          <el-option label="管理后台域名" :value="DomainTypeEnum.ADMIN" />
        </el-select>
      </el-form-item>
      <el-form-item label="关联模块">
        <el-select
          v-model="formData.moduleCode"
          clearable
          placeholder="请选择关联模块（可选）"
          style="width: 100%"
        >
          <el-option label="通用入口" value="" />
          <el-option
            v-for="mod in moduleList"
            :key="mod.name"
            :label="mod.title"
            :value="mod.name"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="关联公众号">
        <el-select
          v-model="formData.mpAccountId"
          clearable
          filterable
          placeholder="可选：显式绑定公众号"
          style="width: 100%"
        >
          <el-option
            v-for="acc in mpAccountList"
            :key="acc.id"
            :label="formatMpAccountLabel(acc)"
            :value="acc.id"
          />
        </el-select>
        <div v-if="formData.domainType === DomainTypeEnum.H5" class="form-tip">
          不填则按模块专用号 → 全平台号选号；一号多域名：多条域名选同一公众号
        </div>
      </el-form-item>
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="是否默认入口">
            <el-switch v-model="formData.isDefault" />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="状态">
            <el-switch
              v-model="formData.status"
              :active-value="1"
              :inactive-value="0"
              active-text="启用"
              inactive-text="禁用"
            />
          </el-form-item>
        </el-col>
      </el-row>
    </el-form>

    <template #footer>
      <el-button :disabled="formLoading" @click="handleClose">取消</el-button>
      <el-button type="primary" :loading="formLoading" @click="handleSubmit">确定</el-button>
    </template>
  </el-dialog>
</template>

<script lang="ts" setup>
import * as SysTenantDomainApi from '@/api/system/tenantDomain/index'
import * as ScenicTenantDomainApi from '@/api/scenic/tenantDomain'
import { DomainTypeEnum } from '@/api/system/tenantDomain/index'
import { getModuleKey } from '@/hooks/useModuleKey'
import * as ModuleApi from '@/api/system/module/index'
import * as MpAccountApi from '@/api/mp/account'

// 按模块选择接口：scenic/food 走模块级接口（只配置当前模块数据），其余走系统接口
const TenantDomainApi =
  getModuleKey() === 'scenic'
    ? ScenicTenantDomainApi
    : SysTenantDomainApi

defineOptions({ name: 'SystemTenantDomainForm' })

const emit = defineEmits(['success'])
const dialogVisible = ref(false)
const dialogTitle = ref('')
const formRef = ref()
const formLoading = ref(false)
const isUpdate = ref(false)
const currentTenantId = ref<number>()

const moduleList = ref<ModuleApi.ModuleVO[]>([])
const mpAccountList = ref<MpAccountApi.AccountVO[]>([])

const formData = reactive<SysTenantDomainApi.TenantDomainVO>({
  tenantId: 0,
  domain: '',
  domainType: DomainTypeEnum.H5,
  moduleCode: undefined,
  mpAccountId: undefined,
  isDefault: false,
  status: 1
})

const formRules = {
  domain: [{ required: true, message: '域名不能为空', trigger: 'blur' }],
  domainType: [{ required: true, message: '域名类型不能为空', trigger: 'change' }],
  status: [{ required: true, message: '状态不能为空', trigger: 'blur' }]
}

const formatMpAccountLabel = (acc: MpAccountApi.AccountVO) => {
  const name = acc.name || `账号#${acc.id}`
  return (acc as any).appId ? `${name} (${(acc as any).appId})` : name
}

/** 打开弹窗 */
const open = async (
  type: string,
  tenantId: number,
  id?: number,
  defaultDomainType?: DomainTypeEnum
) => {
  resetForm()
  currentTenantId.value = tenantId
  formData.tenantId = tenantId
  if (defaultDomainType) {
    formData.domainType = defaultDomainType
  }

  // 加载模块列表、公众号列表
  try {
    const [modules, accounts] = await Promise.all([
      ModuleApi.getEnabledModuleList(),
      MpAccountApi.getSimpleAccountList()
    ])
    moduleList.value = modules || []
    mpAccountList.value = accounts || []
  } catch {
    moduleList.value = []
    mpAccountList.value = []
  }

  if (type === 'create') {
    dialogTitle.value = '新增域名绑定'
    isUpdate.value = false
  } else if (type === 'update' && id) {
    dialogTitle.value = '修改域名绑定'
    isUpdate.value = true
    await loadDetail(id)
  }
  dialogVisible.value = true
}

/** 加载详情 */
const loadDetail = async (id: number) => {
  try {
    const data = await TenantDomainApi.getTenantDomain(id)
    Object.assign(formData, data)
  } catch {
    // 加载失败
  }
}

/** 重置表单 */
const resetForm = () => {
  formRef.value?.resetFields()
  formData.domain = ''
  formData.domainType = DomainTypeEnum.H5
  formData.moduleCode = undefined
  formData.mpAccountId = undefined
  formData.isDefault = false
  formData.status = 1
}

/** 提交 */
const handleSubmit = async () => {
  try {
    await formRef.value?.validate()
  } catch {
    return
  }

  formLoading.value = true
  try {
    if (isUpdate.value) {
      await TenantDomainApi.updateTenantDomain(formData)
      ElMessage.success('修改成功')
    } else {
      await TenantDomainApi.createTenantDomain(formData)
      ElMessage.success('新增成功')
    }
    handleClose()
    emit('success')
  } finally {
    formLoading.value = false
  }
}

/** 关闭弹窗 */
const handleClose = () => {
  dialogVisible.value = false
  resetForm()
}

defineExpose({ open })
</script>

<style lang="scss" scoped>
.form-tip {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--el-text-color-secondary);
}
</style>
